// One recipe opened from the ledger: what it answers, its parameters and
// code, the last verification, the newest runs, and the admin actions
// (Disable / Enable / Activate with a reason, Verify now).

import type { RecipeDetail as RecipeDetailRead, RecipeState } from '../../services/fastpathQuality';
import { ms, whenUtc } from './format';
import styles from '../FastpathQualityPage.module.css';

interface Props {
  recipe: RecipeDetailRead;
  canAct: boolean;
  busy: boolean;
  note: string | null;
  onSetState: (to: RecipeState) => void;
  onVerify: () => void;
}

/** The state moves the page offers for a recipe in `state` (the FSM's legal edges an admin owns). */
export function stateActions(state: RecipeState): Array<{ to: RecipeState; label: string; danger?: boolean; primary?: boolean }> {
  switch (state) {
    case 'draft':
      return [{ to: 'disabled', label: 'Disable', danger: true }];
    case 'verified':
      return [{ to: 'active', label: 'Activate', primary: true }, { to: 'disabled', label: 'Disable', danger: true }];
    case 'active':
      return [{ to: 'disabled', label: 'Disable', danger: true }];
    case 'disabled':
      return [{ to: 'active', label: 'Enable', primary: true }];
    default:
      return [];
  }
}

function pretty(value: unknown): string {
  try {
    return JSON.stringify(value, null, 2);
  } catch {
    return String(value);
  }
}

export function RecipeDetail({ recipe, canAct, busy, note, onSetState, onVerify }: Props) {
  const v = recipe.verification;
  return (
    <section className={styles.panel} aria-label="Recipe detail" data-testid={`recipe-detail-${recipe.id}`}>
      <div className={styles.panelHead}>
        <h2>
          <code className={styles.tag}>{recipe.slug}</code> v{recipe.version} · {recipe.name}{' '}
          <span className={`${styles.chip} ${styles[`recipe_${recipe.state}`] ?? ''}`}>{recipe.state}</span>
        </h2>
        {canAct ? (
          <div className={styles.actions}>
            {stateActions(recipe.state).map((a) => (
              <button
                key={a.to}
                type="button"
                className={`${styles.actionBtn} ${a.danger ? styles.actionDanger : ''} ${a.primary ? styles.actionPrimary : ''}`}
                onClick={() => onSetState(a.to)}
                disabled={busy}
              >
                {a.label}
              </button>
            ))}
            {recipe.state !== 'superseded' ? (
              <button type="button" className={styles.actionBtn} onClick={onVerify} disabled={busy}>
                Verify now
              </button>
            ) : null}
          </div>
        ) : null}
      </div>
      {note ? <div className={styles.runNote}>{note}</div> : null}
      <div className={styles.summary}>
        <span>{recipe.description || 'No description.'}</span>
        <span className={styles.meta}>
          entry <code className={styles.tag}>{recipe.playbook_entry_id}</code> · profile {recipe.profile_id} · {recipe.language} ·
          sha {recipe.code_sha.slice(0, 12)} · by {recipe.author_kind}{recipe.author_profile_id ? ` (${recipe.author_profile_id})` : ''} ·
          {' '}{recipe.run_count} run{recipe.run_count === 1 ? '' : 's'} · {recipe.consecutive_failures} consecutive failure{recipe.consecutive_failures === 1 ? '' : 's'} ·
          last run {whenUtc(recipe.last_run_at)} · last verified {whenUtc(recipe.last_verified_at)}
          {recipe.superseded_by ? ` · superseded by ${recipe.superseded_by.slice(0, 8)}` : ''}
        </span>
        {v?.last_transition ? (
          <span className={styles.meta}>
            last transition: {v.last_transition.from} → {v.last_transition.to} by {v.last_transition.actor} at {whenUtc(v.last_transition.at)} — {v.last_transition.reason}
          </span>
        ) : null}
      </div>

      <div className={styles.detailGrid}>
        <div>
          <h3>Question examples</h3>
          {recipe.question_examples.length ? (
            <ul className={styles.examples}>{recipe.question_examples.map((q, i) => <li key={i}>{q}</li>)}</ul>
          ) : <p className={styles.meta}>none</p>}
        </div>
        <div>
          <h3>Params schema</h3>
          <pre className={styles.pre}>{pretty(recipe.params_schema)}</pre>
        </div>
        <div>
          <h3>Verification</h3>
          <pre className={styles.pre}>{v ? pretty(v) : 'never verified'}</pre>
        </div>
      </div>

      <h3 className={styles.meta}>Code ({recipe.language})</h3>
      <pre className={styles.code} data-testid="recipe-code">{recipe.code}</pre>

      <h3 className={styles.meta}>Last {recipe.runs.length} run{recipe.runs.length === 1 ? '' : 's'}</h3>
      {recipe.runs.length === 0 ? (
        <p className={styles.empty}>This recipe has not run yet.</p>
      ) : (
        <div className={styles.tableWrap}>
          <table className={styles.table} aria-label="Recipe runs">
            <thead>
              <tr>
                <th>ran</th>
                <th>trigger</th>
                <th>outcome</th>
                <th>params</th>
                <th className={styles.num}>elapsed</th>
                <th className={styles.num}>rows</th>
                <th className={styles.num}>bytes</th>
                <th>error</th>
                <th>conversation</th>
              </tr>
            </thead>
            <tbody>
              {recipe.runs.map((run) => (
                <tr key={run.id} data-outcome={run.outcome}>
                  <td>{whenUtc(run.created_at)}</td>
                  <td>{run.trigger}</td>
                  <td><span className={`${styles.chip} ${styles[`outcome_${run.outcome}`] ?? ''}`}>{run.outcome}</span></td>
                  <td className={styles.wrap}><code className={styles.tag}>{JSON.stringify(run.params)}</code></td>
                  <td className={styles.num}>{ms(run.elapsed_ms)}</td>
                  <td className={styles.num}>{run.rows_scanned ?? '–'}</td>
                  <td className={styles.num}>{run.result_bytes.toLocaleString()}</td>
                  <td className={styles.wrap}>{run.error ?? '–'}</td>
                  <td>{run.conversation_id ? <code className={styles.tag}>{run.conversation_id.slice(0, 8)}</code> : '–'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}
